/* ============================================================
   Sentry initialization
   ------------------------------------------------------------
   Captures crashes + uncaught promise rejections in the WebView.
   No-op unless VITE_SENTRY_DSN is set in the environment —
   so this safely ships to production even before you've created
   a Sentry project.

   To activate:
     1. Create a project at sentry.io
     2. Copy the DSN
     3. Add to .env.local (or your build environment):
          VITE_SENTRY_DSN=https://xxx@oYYY.ingest.sentry.io/ZZZ
     4. Rebuild
   ============================================================ */

import * as Sentry from '@sentry/react'

const DSN = import.meta.env.VITE_SENTRY_DSN

let initialized = false

export function initSentry() {
  if (initialized) return
  if (!DSN) return // no-op without a DSN
  initialized = true

  Sentry.init({
    dsn: DSN,
    environment: import.meta.env.MODE || 'development',
    // Keep release tied to the build so we can correlate crashes
    // with commits. Vite exposes the app version via package.json
    // through define() at build time if configured; fallback: undefined.
    release: import.meta.env.VITE_APP_VERSION,

    // Performance monitoring sample rate — 10% in prod, full in dev
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session replay — off by default (bandwidth / privacy). Flip on
    // if you want visual replay of crashes.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    // Strip personal data by default — we can add an integration
    // later if we need user context.
    sendDefaultPii: false,

    // Ignore noisy browser errors we can't fix
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
    ],

    beforeSend(event) {
      // Drop events in dev unless explicitly enabled, so local testing
      // doesn't pollute the production issues list.
      if (!import.meta.env.PROD && !import.meta.env.VITE_SENTRY_DEV_ENABLED) {
        return null
      }
      return event
    },
  })
}

export function captureException(error, context) {
  if (!initialized) return
  try {
    Sentry.captureException(error, context ? { extra: context } : undefined)
  } catch {}
}

export function captureMessage(message, level = 'info') {
  if (!initialized) return
  try {
    Sentry.captureMessage(message, level)
  } catch {}
}
